'use client';

import { useEffect, useState } from 'react';
import type { CommunityMatchBlockProps } from '../types';
import { theme, sectionColors, buttonVars } from '../theme';
import { SpamGuardInputs, useSpamGuard } from './useSpamGuard';
import { FIELD, PRIMARY_BTN, pageUrl, readBrowserId } from './calcShared';
import { MATCH_SMS_CONSENT_TEXT as APPROVED_MATCH_CONSENT_TEXT, approvedSmsConsentText, smsConsentFields } from './smsConsent';

/**
 * Community match quiz — V3.2 (docs/V3_LAUNCH_PLAN.md §5 "Compare + Match",
 * docs/LEAD_GEN_MACHINE_BUILD_PLAN.md §11.3). CLIENT block, framework-agnostic (plain elements):
 *
 *   quiz     six steps — budget, commute anchor (optional), home type, timeline, priorities
 *            (objective facts only), "do you own a home now?" (+ current ZIP)
 *            → POST same-origin /api/community-match → the run id + a TEASER
 *   teaser   the top three community NAMES with a match band; the reasons and stats are
 *            withheld (blurred placeholders carry no data) until the gate
 *   gate     name + email (+ optional phone with an UNCHECKED SMS-consent box that is never
 *            pre-ticked) → POST /api/community-match/unlock → contact, buyer lead (+ seller lead
 *            when they own a home locally — the gateway decides), drip, speed-to-lead
 *   results  each match with its score, the factual reasons the gateway built, the stored
 *            stats, and a link to the community page
 *
 * The candidate names shown while answering come from /api/community-match/candidates (best
 * effort). Analytics: none from the block — the gateway records community_match_run /
 * community_match_unlocked itself.
 */

/**
 * Default TCPA consent checkbox copy — version `v1-2026-09` (docs/SMS_CONSENT.md). The wording and
 * its version are the platform-owned contract in ./smsConsent.ts: an agent-configured
 * `consentText` is only rendered when it is one of the approved wordings, and the words shown are
 * posted with the capture so the gateway derives the stored version id from them.
 */
export const MATCH_SMS_CONSENT_TEXT = APPROVED_MATCH_CONSENT_TEXT;

export type QuizStep = 'budget' | 'commute' | 'home_type' | 'timeline' | 'priorities' | 'own_home';
export const QUIZ_STEPS: readonly QuizStep[] = ['budget', 'commute', 'home_type', 'timeline', 'priorities', 'own_home'];

export type HomeType = 'single_family' | 'condo' | 'townhouse' | 'any';
export type Timeline = '0-3' | '3-6' | '6-12' | '12+';
export type PriorityKey = 'walkability' | 'parks' | 'near_water' | 'hoa_none' | 'new_construction' | 'low_dom';

/** The priorities are objective, stored facts — never a characterization of a place or its people. */
export const PRIORITY_OPTIONS: ReadonlyArray<{ key: PriorityKey; label: string; help: string }> = [
  { key: 'walkability', label: 'Walkable', help: 'Scored by Walk Score' },
  { key: 'parks', label: 'Parks nearby', help: 'Parks listed for the community' },
  { key: 'near_water', label: 'Near the water', help: 'Waterfront, beach or marina access listed' },
  { key: 'hoa_none', label: 'No HOA', help: 'No HOA fee on file' },
  { key: 'new_construction', label: 'New construction', help: 'New-construction listings active' },
  { key: 'low_dom', label: 'Homes sell quickly', help: 'Low median days on market' },
];

export const HOME_TYPE_OPTIONS: ReadonlyArray<{ value: HomeType; label: string }> = [
  { value: 'single_family', label: 'Single-family home' },
  { value: 'condo', label: 'Condo' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'any', label: 'Open to any' },
];

export const TIMELINE_OPTIONS: ReadonlyArray<{ value: Timeline; label: string }> = [
  { value: '0-3', label: 'In the next 3 months' },
  { value: '3-6', label: '3–6 months' },
  { value: '6-12', label: '6–12 months' },
  { value: '12+', label: 'A year or more out' },
];

export const COMMUTE_MAX_OPTIONS: readonly number[] = [15, 30, 45, 60];

export interface QuizAnswers {
  budget_max: string;
  commute_address: string;
  commute_max_minutes: string;
  home_type: HomeType;
  timeline: Timeline;
  priorities: PriorityKey[];
  own_home_now: boolean | null;
  current_zip: string;
}

export const EMPTY_ANSWERS: QuizAnswers = { budget_max: '', commute_address: '', commute_max_minutes: '30', home_type: 'any', timeline: '3-6', priorities: [], own_home_now: null, current_zip: '' };

/** The API body for POST /api/community-match, built from the form state. Null when the budget is missing. */
export function buildRunPayload(a: QuizAnswers): Record<string, unknown> | null {
  const budget = Number(String(a.budget_max).replace(/[,$\s]/g, ''));
  if (!Number.isFinite(budget) || budget <= 0) return null;
  const address = a.commute_address.trim();
  const maxMinutes = Number(a.commute_max_minutes);
  return {
    answers: {
      budget_max: budget,
      commute_anchor: address ? { address, max_minutes: Number.isFinite(maxMinutes) && maxMinutes > 0 ? maxMinutes : undefined } : null,
      home_type: a.home_type,
      timeline: a.timeline,
      priorities: Array.from(new Set(a.priorities)),
      own_home_now: a.own_home_now === true,
      current_zip: a.own_home_now === true ? a.current_zip.trim() || null : null,
    },
  };
}

export function bandLabel(band: string): string {
  return band === 'high' ? 'Strong match' : band === 'medium' ? 'Possible match' : 'Partial match';
}

interface TeaserItem { slug: string; name: string; score_band: string }
interface ResultItem {
  slug: string; name: string; score: number; score_band: string; reasons: string[]; link: string;
  stats: { median_price: number | null; dom: number | null; months_inventory: number | null; active_count: number | null; walkability_score: number | null; parks_count: number | null; hoa_fee_display: string | null };
  commute: { miles: number; minutes: number } | null;
}
interface Candidate { slug: string; name: string; city: string | null; active_count: number | null }

type Phase = 'quiz' | 'teaser' | 'gate' | 'results';

const fmtMoney = (n: number | null): string => (typeof n === 'number' && Number.isFinite(n) ? `$${Math.round(n).toLocaleString('en-US')}` : '—');
const RADIO = 'flex items-start gap-3 rounded-md border border-black/10 px-3 py-2 text-sm';

export function CommunityMatchBlock(props: CommunityMatchBlockProps & { turnstileSiteKey?: string | null }) {
  const {
    heading = 'Which community fits you?',
    subheading = 'Answer six quick questions and get a personalized match report.',
    buttonText = 'Find my match',
    consentText,
    showPhone = true,
    phoneRequired = false,
  } = props;
  // The section's button colours ride on the section as --blk-btn vars (plan E6).
  const colors = { ...sectionColors(props, { bg: 'transparent', text: theme.text }), ...buttonVars(props) };
  // Platform-owned TCPA copy: an unapproved (agent-edited) wording is never shown, because it
  // could not be recorded against a version id (./smsConsent.ts).
  const shownConsentText = approvedSmsConsentText(consentText, MATCH_SMS_CONSENT_TEXT);

  const [phase, setPhase] = useState<Phase>('quiz');
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswers>(EMPTY_ANSWERS);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [teaser, setTeaser] = useState<TeaserItem[]>([]);
  const [results, setResults] = useState<ResultItem[]>([]);
  const [consent, setConsent] = useState(false);
  // Spam-check inputs for the gate (./useSpamGuard.tsx).
  const guard = useSpamGuard(props.turnstileSiteKey);

  // The site's communities, for "matching against …" while answering. Best effort.
  useEffect(() => {
    let cancelled = false;
    fetch('/api/community-match/candidates')
      .then((r) => (r.ok ? r.json() : null))
      .then((body: { data?: { candidates?: Candidate[] } } | null) => {
        if (!cancelled && body?.data?.candidates) setCandidates(body.data.candidates.slice(0, 10));
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const step = QUIZ_STEPS[stepIndex];
  const set = <K extends keyof QuizAnswers>(key: K, value: QuizAnswers[K]) => setAnswers((a) => ({ ...a, [key]: value }));

  function validateStep(): string | null {
    if (step === 'budget') {
      const n = Number(String(answers.budget_max).replace(/[,$\s]/g, ''));
      if (!Number.isFinite(n) || n <= 0) return 'Please enter the most you would spend.';
    }
    if (step === 'own_home' && answers.own_home_now === null) return 'Please tell us whether you own a home now.';
    if (step === 'own_home' && answers.own_home_now && answers.current_zip.trim() && !/^\d{5}(-\d{4})?$/.test(answers.current_zip.trim())) return 'Please enter a five-digit ZIP, or leave it blank.';
    return null;
  }

  function next() {
    const problem = validateStep();
    if (problem) { setError(problem); return; }
    setError(null);
    if (stepIndex < QUIZ_STEPS.length - 1) setStepIndex(stepIndex + 1);
    else void submitRun();
  }

  function back() {
    setError(null);
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  }

  async function submitRun() {
    const payload = buildRunPayload(answers);
    if (!payload) { setError('Please enter the most you would spend.'); setStepIndex(0); return; }
    setPending(true);
    setError(null);
    try {
      const res = await fetch('/api/community-match', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, browser_id: readBrowserId(), page_url: pageUrl() }),
      });
      const body = (await res.json().catch(() => ({}))) as { data?: { run_id: string; teaser: TeaserItem[] } | null; error?: string | null };
      if (!res.ok || !body.data) throw new Error(body.error || `Request failed: ${res.status}`);
      setRunId(body.data.run_id);
      setTeaser(body.data.teaser ?? []);
      setPhase('teaser');
    } catch (err) {
      setError(err instanceof Error && err.message && !/Request failed/.test(err.message) ? err.message : 'Sorry, we could not score your matches right now. Please try again.');
    } finally {
      setPending(false);
    }
  }

  async function submitGate(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (pending || !runId) return;
    const fd = new FormData(e.currentTarget);
    const name = String(fd.get('name') ?? '').trim();
    const email = String(fd.get('email') ?? '').trim();
    const phone = String(fd.get('phone') ?? '').trim();
    const phoneDigits = phone.replace(/\D/g, '').length;
    if (phoneDigits < 7 && (phoneRequired || phoneDigits > 0)) {
      setError(phoneRequired ? 'Please enter a phone number so we can send your report.' : 'Please enter a valid phone number, or leave it blank.');
      return;
    }
    const spam = guard.fields(e.currentTarget);
    setError(null);
    setPending(true);
    try {
      const res = await fetch('/api/community-match/unlock', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          run_id: runId, name, email, phone: phone || undefined,
          // Consent is for a number: without one there is nothing to consent to. The exact words
          // shown travel with it; the gateway derives the stored version id from them.
          ...smsConsentFields({ checked: consent, hasPhone: phoneDigits > 0, shownText: shownConsentText }),
          browser_id: readBrowserId(), page_url: pageUrl(),
          ...spam,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as { data?: { top: ResultItem[] } | null; error?: string | null };
      if (!res.ok || !body.data) throw new Error(body.error || `Request failed: ${res.status}`);
      setResults(body.data.top ?? []);
      setPhase('results');
    } catch (err) {
      setError(err instanceof Error && err.message && !/Request failed/.test(err.message) ? err.message : 'Sorry, something went wrong unlocking your report. Please try again.');
    } finally {
      guard.reset();
      setPending(false);
    }
  }

  function restart() {
    setPhase('quiz'); setStepIndex(0); setAnswers(EMPTY_ANSWERS); setRunId(null); setTeaser([]); setResults([]); setError(null); setConsent(false);
  }

  // The button ROLE, via the shared class — not `theme.primary` inline. Inline colours also beat
  // the `.blk-btn:hover` rule, so these three would have ignored Button Primary BG/Text/Hover
  // while the registry said it controlled every form-submit button.
  const primaryBtn = 'blk-btn';

  return (
    <section id="community-match" className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }} data-block="community-match" data-phase={phase}>
      <div className="mx-auto max-w-3xl">
        <h2 className="mb-2 text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>{heading}</h2>
        {subheading ? <p className="mb-6 opacity-80">{subheading}</p> : null}
        {error ? <p className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{error}</p> : null}

        {phase === 'quiz' ? (
          <form onSubmit={(e) => { e.preventDefault(); next(); }} className="space-y-5 rounded-lg border border-black/10 p-5" data-step={step} noValidate>
            <p className="text-xs uppercase tracking-wide opacity-70">Question {stepIndex + 1} of {QUIZ_STEPS.length}</p>

            {step === 'budget' ? (
              <label htmlFor="cm-budget" className="block text-sm">
                <span className="mb-1 block font-medium">What is the most you would spend?</span>
                <span className="relative block">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 opacity-60">$</span>
                  <input id="cm-budget" name="budget_max" type="number" inputMode="numeric" min={0} step={10000} placeholder="650,000" value={answers.budget_max} onChange={(e) => set('budget_max', e.currentTarget.value)} className={`${FIELD} pl-7`} />
                </span>
                <span className="mt-1 block text-xs opacity-70">We compare it with each community&apos;s median sale price.</span>
              </label>
            ) : null}

            {step === 'commute' ? (
              <div className="space-y-3">
                <label htmlFor="cm-commute" className="block text-sm">
                  <span className="mb-1 block font-medium">Where do you commute to? <span className="font-normal opacity-70">(optional)</span></span>
                  <input id="cm-commute" name="commute_address" type="text" placeholder="An address, workplace or downtown" value={answers.commute_address} onChange={(e) => set('commute_address', e.currentTarget.value)} className={FIELD} autoComplete="street-address" />
                  <span className="mt-1 block text-xs opacity-70">Drive times are estimated from straight-line distance at 30 mph — a rough guide, not a route.</span>
                </label>
                <label htmlFor="cm-commute-max" className="block text-sm">
                  <span className="mb-1 block font-medium">Longest drive you would accept</span>
                  <select id="cm-commute-max" name="commute_max_minutes" value={answers.commute_max_minutes} onChange={(e) => set('commute_max_minutes', e.currentTarget.value)} className={FIELD}>
                    {COMMUTE_MAX_OPTIONS.map((m) => <option key={m} value={String(m)}>{m} minutes</option>)}
                  </select>
                </label>
              </div>
            ) : null}

            {step === 'home_type' ? (
              <fieldset className="space-y-2">
                <legend className="mb-1 text-sm font-medium">What kind of home?</legend>
                {HOME_TYPE_OPTIONS.map((o) => (
                  <label key={o.value} className={RADIO}>
                    <input type="radio" name="home_type" value={o.value} checked={answers.home_type === o.value} onChange={() => set('home_type', o.value)} className="mt-0.5" />
                    <span>{o.label}</span>
                  </label>
                ))}
              </fieldset>
            ) : null}

            {step === 'timeline' ? (
              <fieldset className="space-y-2">
                <legend className="mb-1 text-sm font-medium">When are you looking to move?</legend>
                {TIMELINE_OPTIONS.map((o) => (
                  <label key={o.value} className={RADIO}>
                    <input type="radio" name="timeline" value={o.value} checked={answers.timeline === o.value} onChange={() => set('timeline', o.value)} className="mt-0.5" />
                    <span>{o.label}</span>
                  </label>
                ))}
              </fieldset>
            ) : null}

            {step === 'priorities' ? (
              <fieldset className="space-y-2">
                <legend className="mb-1 text-sm font-medium">What matters most? <span className="font-normal opacity-70">(pick any)</span></legend>
                {PRIORITY_OPTIONS.map((o) => {
                  const on = answers.priorities.includes(o.key);
                  return (
                    <label key={o.key} className={RADIO}>
                      <input type="checkbox" name="priorities" value={o.key} checked={on} onChange={() => set('priorities', on ? answers.priorities.filter((k) => k !== o.key) : [...answers.priorities, o.key])} className="mt-0.5" />
                      <span>{o.label}<span className="block text-xs opacity-70">{o.help}</span></span>
                    </label>
                  );
                })}
              </fieldset>
            ) : null}

            {step === 'own_home' ? (
              <fieldset className="space-y-2">
                <legend className="mb-1 text-sm font-medium">Do you own a home now?</legend>
                <label className={RADIO}>
                  <input type="radio" name="own_home_now" value="yes" checked={answers.own_home_now === true} onChange={() => set('own_home_now', true)} className="mt-0.5" />
                  <span>Yes</span>
                </label>
                <label className={RADIO}>
                  <input type="radio" name="own_home_now" value="no" checked={answers.own_home_now === false} onChange={() => set('own_home_now', false)} className="mt-0.5" />
                  <span>No</span>
                </label>
                {answers.own_home_now ? (
                  <label htmlFor="cm-zip" className="block pt-2 text-sm">
                    <span className="mb-1 block font-medium">Your current ZIP <span className="font-normal opacity-70">(optional)</span></span>
                    <input id="cm-zip" name="current_zip" type="text" inputMode="numeric" maxLength={10} placeholder="33458" value={answers.current_zip} onChange={(e) => set('current_zip', e.currentTarget.value)} className={FIELD} autoComplete="postal-code" />
                  </label>
                ) : null}
              </fieldset>
            ) : null}

            <div className="flex flex-wrap items-center gap-3 pt-1">
              {stepIndex > 0 ? <button type="button" onClick={back} className="text-sm underline opacity-70" data-action="back">Back</button> : null}
              <button type="submit" disabled={pending} className={`${primaryBtn} ${PRIMARY_BTN}`} data-action="next">
                {pending ? 'Scoring your matches…' : stepIndex === QUIZ_STEPS.length - 1 ? buttonText : 'Next'}
              </button>
            </div>

            {candidates.length ? (
              <p className="text-xs opacity-70" data-candidates={candidates.length}>
                Matching against {candidates.map((c) => c.name).join(', ')}.
              </p>
            ) : null}
          </form>
        ) : null}

        {phase === 'teaser' || phase === 'gate' ? (
          <div className="rounded-lg border border-black/10 p-5" data-teaser={teaser.length}>
            <p className="text-xs uppercase tracking-wide opacity-70">Your top matches</p>
            <ol className="mt-3 space-y-3">
              {teaser.map((t, i) => (
                <li key={t.slug} className="rounded-md border border-black/10 p-3" data-teaser-item={t.slug}>
                  <p className="font-medium">{i + 1}. {t.name} <span className="ml-2 rounded-full bg-black/5 px-2 py-0.5 text-xs">{bandLabel(t.score_band)}</span></p>
                  {/* Placeholder lines only — no reasons or stats are in the page until the gate. */}
                  <div className="mt-2 space-y-1 select-none blur-sm" aria-hidden="true">
                    <div className="h-3 w-3/4 rounded bg-black/10" />
                    <div className="h-3 w-1/2 rounded bg-black/10" />
                  </div>
                </li>
              ))}
            </ol>
            {teaser.length === 0 ? <p className="mt-3 text-sm opacity-70">No communities matched yet — try widening your budget.</p> : null}

            {phase === 'teaser' ? (
              <button type="button" onClick={() => setPhase('gate')} className={`${primaryBtn} ${PRIMARY_BTN} mt-5`} data-gate="closed">
                Unlock my full report
              </button>
            ) : (
              <form onSubmit={submitGate} className="mt-5 space-y-3 rounded-lg border border-black/10 p-4" data-gate="open">
                <p className="text-sm opacity-80">Tell us where to send your match report — why each community matched, with the numbers.</p>
                <input type="text" name="name" placeholder="Your name" required autoComplete="name" className={FIELD} />
                <input type="email" name="email" placeholder="you@example.com" required autoComplete="email" className={FIELD} />
                {showPhone || phoneRequired ? (
                  <>
                    <input type="tel" name="phone" placeholder={phoneRequired ? 'Phone' : 'Phone (optional)'} required={phoneRequired} autoComplete="tel" className={FIELD} />
                    <label className="flex items-start gap-2 text-sm opacity-90">
                      <input type="checkbox" name="consent_sms" checked={consent} onChange={(e) => setConsent(e.currentTarget.checked)} className="mt-1" />
                      <span>{shownConsentText}</span>
                    </label>
                  </>
                ) : null}
                <SpamGuardInputs guard={guard} />
                <div>
                  <button type="submit" disabled={pending} className={`${primaryBtn} ${PRIMARY_BTN}`}>
                    {pending ? 'Unlocking…' : 'Show my matches'}
                  </button>
                  <button type="button" onClick={() => { setError(null); setPhase('teaser'); }} className="ml-3 text-sm underline opacity-70">Cancel</button>
                </div>
              </form>
            )}
          </div>
        ) : null}

        {phase === 'results' ? (
          <div className="space-y-4" data-results={results.length}>
            {results.map((r, i) => (
              <article key={r.slug} className="rounded-lg border border-black/10 p-5" data-result={r.slug}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-lg font-semibold" style={{ fontFamily: theme.headingFont }}>{i + 1}. {r.name}</h3>
                  <span className="text-sm opacity-80">{bandLabel(r.score_band)} · {r.score}/100</span>
                </div>
                {r.reasons.length ? (
                  <ul className="mt-3 list-disc space-y-1 pl-5 text-sm">
                    {r.reasons.map((reason) => <li key={reason}>{reason}</li>)}
                  </ul>
                ) : null}
                <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
                  <div><dt className="text-xs uppercase tracking-wide opacity-60">Median sale</dt><dd>{fmtMoney(r.stats.median_price)}</dd></div>
                  <div><dt className="text-xs uppercase tracking-wide opacity-60">Active now</dt><dd>{r.stats.active_count ?? '—'}</dd></div>
                  <div><dt className="text-xs uppercase tracking-wide opacity-60">Days on market</dt><dd>{r.stats.dom ?? '—'}</dd></div>
                  <div><dt className="text-xs uppercase tracking-wide opacity-60">Months of inventory</dt><dd>{r.stats.months_inventory ?? '—'}</dd></div>
                  <div><dt className="text-xs uppercase tracking-wide opacity-60">Walk Score</dt><dd>{r.stats.walkability_score ?? '—'}</dd></div>
                  <div><dt className="text-xs uppercase tracking-wide opacity-60">HOA</dt><dd>{r.stats.hoa_fee_display ?? '—'}</dd></div>
                </dl>
                {r.commute ? <p className="mt-3 text-xs opacity-70">Estimated drive: about {r.commute.minutes} min ({r.commute.miles} mi straight-line at 30 mph).</p> : null}
                <a href={r.link} className="mt-4 inline-block text-sm font-medium underline">See {r.name}</a>
              </article>
            ))}
            {results.length === 0 ? <p className="text-sm opacity-70">We could not rank any community for those answers.</p> : null}
            <button type="button" onClick={restart} className="text-sm underline opacity-70" data-action="restart">Start over</button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
